import React, { FC } from 'react';
import {
  Box,
  Button,
  Container,
  Grid,
  Stack,
} from '@mui/material';
import StyledBox from 'views/rebranded-module/OffRoadToursLandingViewV2/components/OffRoadUTVPromoSection/OffRoadUTVPromoSection.styles';
import { useContent } from 'views/rebranded-module/common/context/useContent';
import {
  ImgBox,
  Typography700,
} from 'views/rebranded-module/common/components';
import useAdabtiveMultiSizeImage from 'views/common/hooks/useAdabtiveMultiSizeImage';
import Typography500 from 'views/common/components/UI/Typography500';
import NavigatorLink from 'views/rebranded-module/common/components/Logical/NavigatorLink';
import {
  RiAlertLine,
  RiCalendarLine,
} from 'react-icons/ri';
import PATHS from 'lib/navigation/paths';
import { OFF_ROAD_LANDING_REF, UTV_2_DAY_PROMO_AVAILABLE_DATES } from 'views/rebranded-module/OffRoadToursLandingViewV2/utils/constants';

interface UTVPromoTour {
  name: string;
  slug: string;
  tour_type: { slug: string };
}

interface OffRoadUTVPromoSectionProps {
  utv2DayPromoTour: UTVPromoTour;
}

const OffRoadUTVPromoSection: FC<OffRoadUTVPromoSectionProps> = (props) => {
  const { utv2DayPromoTour } = props;
  const content = useContent('OffRoadLandingPage');
  const getSizedImage = useAdabtiveMultiSizeImage();
  const tourLink = PATHS.tour.index(utv2DayPromoTour.tour_type.slug, utv2DayPromoTour.slug, {
    ref: OFF_ROAD_LANDING_REF,
  });

  return (
    <StyledBox>
      <Box className="ORUTVBGPattern">
        <ImgBox
          image={{
            src: getSizedImage(content.utvPromoPatternBackground),
            alt: content.utvPromoPatternBackground.alt,
          }}
          width="100%"
          height="100%"
          unoptimized
        />
      </Box>
      <Container className="ORUTVPromoContainer" maxWidth="xl">
        <Grid
          container
          spacing={8}
          alignItems="center">
          <Grid
            item
            className="ORUTVPromoContentGridItem"
            lg={6}
            md={12}
            sm={12}>
            <Stack className="ORUTVPromoContentStack">
              <Typography700 component="h3" variant="h3">
                <Box component="span" display="block">
                  {content.utvPromoTitleLine1}
                </Box>
                <Box component="span" color="primary.main">
                  {content.utvPromoTitleAccent}
                </Box>
                <Box component="span" display="block">
                  {content.utvPromoTitleLine2}
                </Box>
              </Typography700>
              <Typography500
                variant="body1"
                component="p"
                color="text.secondary">
                {content.utvPromoDescription}
              </Typography500>
              <Box className="ORUTVPromoDatesBlock">
                <Box className="ORUTVPromoDatesInner">
                  <Box className="ORUTVPromoDatesRow">
                    <RiCalendarLine className="ORUTVPromoDatesRowIcon" />
                    <Typography700
                      fontWeight={700}
                      component="p"
                      variant="body1">
                      {content.utvPromoAvailableDatesHeading}
                    </Typography700>
                  </Box>
                  <Box className="ORUTVPromoDatesRow">
                    <Box className="ORUTVPromoDateChips">
                      {UTV_2_DAY_PROMO_AVAILABLE_DATES.map((label) => (
                        <Box
                          className="ORUTVPromoDateChip"
                          component="span"
                          key={label}>
                          <Typography500
                            className="ORUTVPromoDateChipLabel"
                            component="span"
                            variant="body3">
                            {label}
                          </Typography500>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Box>
                <Box className="ORUTVPromoDatesRow">
                  <RiAlertLine className="ORUTVPromoDatesRowIcon" />
                  <Typography500
                    className="ORUTVPromoSpotsLimited"
                    component="p"
                    variant="body1">
                    {content.utvPromoSpotsLimited}
                  </Typography500>
                </Box>
              </Box>
              <Button
                className="ORUTVPromoBookBtn"
                component={NavigatorLink}
                href={tourLink as any}
                data-testid={`offroad-2-day-utv-promo-tour-${encodeURIComponent(utv2DayPromoTour.slug)}`}
                variant="contained"
                color="primary"
                size="large">
                {content.utvBookNowBtnLabel}
              </Button>
            </Stack>

          </Grid>
          <Grid
            item
            className="ORUTVPromoImageGridItem"
            lg={6}
            md={12}
            sm={12}>
            <Box className="ORUTVPromoImage">
              <ImgBox
                image={{
                  src: getSizedImage(content.utvPromoTourBackground),
                  alt: content.utvPromoTourBackground.alt,
                }}
                width="100%"
                height="100%"
                unoptimized
              />
            </Box>
          </Grid>
        </Grid>
      </Container>
    </StyledBox>
  );
};

export { OffRoadUTVPromoSection };
